import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from '../../hooks/useDarkMode';
import {
  IcoMenu, IcoClose, IcoChevronDown, IcoSun, IcoMoon,
  IcoArrow
} from '../../assets/icons';

/* ── scrolled hook ─────────────────────────────────────── */
function useScrolled(t = 16) {
  const [s, set] = useState(false);
  useEffect(() => {
    const fn = () => set(window.scrollY > t);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, [t]);
  return s;
}

/* ── Logo ───────────────────────────────────────────────── */
function Logo({ onClick, white }) {
  return (
    <button onClick={onClick} className="flex items-center gap-3 flex-shrink-0 group">
      <img
        src="/fdc-logo.png"
        alt="Fundação Ngana Zenza"
        className="h-9 w-auto flex-shrink-0 transition-all duration-300"
        style={{ filter: white ? 'brightness(0) invert(1)' : 'none' }}
      />
      <div className="hidden sm:block text-left leading-[1.1]">
        <div className={`text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors ${
          white ? 'text-white/60' : 'text-brand-500'
        }`}>
          Fundação
        </div>
        <div className={`text-[15px] font-bold transition-colors group-hover:opacity-80 ${
          white ? 'text-white' : 'text-brand-700 dark:text-brand-300'
        }`}>
          Ngana Zenza
        </div>
      </div>
    </button>
  );
}

/* ── Mega-menu data ─────────────────────────────────────── */
const megaData = {
  quemSomos: {
    cols: [
      {
        head_pt: 'A Fundação',
        head_en: 'The Foundation',
        links: [
          { pt: 'A Nossa História',   en: 'Our History',      href: '/quem-somos' },
          { pt: 'Missão e Visão',     en: 'Mission & Vision', href: '/quem-somos' },
          { pt: 'A Fundadora',        en: 'The Founder',      href: '/quem-somos' },
          { pt: 'Os Nossos Valores',  en: 'Our Values',       href: '/quem-somos' },
          { pt: 'Áreas de Actuação',  en: 'Areas of Action',  href: '/quem-somos' },
          { pt: 'Cronologia',         en: 'Timeline',         href: '/quem-somos' },
        ],
      },
    ],
    feat: {
      title_pt: 'Criada em 20 de\nFevereiro de 2020',
      title_en: 'Created on\n20 February 2020',
      desc_pt:  'Fundada pela Primeira-Dama Ana Dias Lourenço para o desenvolvimento das comunidades mais vulneráveis de Angola.',
      desc_en:  'Founded by First Lady Ana Dias Lourenço for the development of Angola\'s most vulnerable communities.',
      href:     '/quem-somos',
    },
  },
  programas: {
    cols: [
      {
        head_pt: 'Programas de Saúde',
        head_en: 'Health Programmes',
        links: [
          { pt: 'Tata Uhayele – Cuide da Sua Saúde', en: 'Tata Uhayele – Health',          href: '/programas' },
          { pt: 'Geração Sem Sida até 2030',          en: 'Generation Without AIDS 2030',   href: '/programas' },
          { pt: 'Cirurgias Ortopédicas – Pé Boto',   en: 'Orthopaedic Surgery – Clubfoot', href: '/programas' },
        ],
      },
      {
        head_pt: 'Programas Comunitários',
        head_en: 'Community Programmes',
        links: [
          { pt: 'PDIC – Desenvolvimento Integrado', en: 'PDIC – Integrated Development', href: '/programas' },
          { pt: 'Seja Mulher',                      en: 'Seja Mulher',                   href: '/programas' },
          { pt: 'Campus Juventude',                 en: 'Youth Campus',                  href: '/programas' },
          { pt: 'Dikota E6.0',                      en: 'Dikota E6.0',                   href: '/programas' },
          { pt: 'Revieng Angola',                   en: 'Revieng Angola',                href: '/programas' },
        ],
      },
    ],
    feat: {
      title_pt: '8 Programas\nActivos',
      title_en: '8 Active\nProgrammes',
      desc_pt:  'Saúde, educação, empoderamento feminino, juventude, idosos e assistência humanitária.',
      desc_en:  'Health, education, female empowerment, youth, elderly and humanitarian assistance.',
      href:     '/programas',
    },
  },
  accoes: {
    cols: [
      {
        head_pt: 'Galeria',
        head_en: 'Gallery',
        links: [
          { pt: 'Fotos em Campo',            en: 'Field Photos',          href: '/accoes' },
          { pt: 'Vídeos Institucionais',     en: 'Institutional Videos',  href: '/accoes' },
          { pt: 'PDIC – Comunidade do Toco', en: 'PDIC – Toco Community', href: '/accoes' },
          { pt: 'Histórias de Impacto',      en: 'Impact Stories',        href: '/accoes' },
        ],
      },
    ],
    feat: {
      title_pt: 'Galeria de\nImpacto Social',
      title_en: 'Social Impact\nGallery',
      desc_pt:  'Fotos e vídeos das nossas acções em todo o território angolano.',
      desc_en:  'Photos and videos of our actions across Angola.',
      href:     '/accoes',
    },
  },
};

/* ── Mega Panel ────────────────────────────────────────── */
function MegaPanel({ menuKey, isPt, onNavigate, onClose }) {
  const m = megaData[menuKey];
  if (!m) return null;

  return (
    <div className="absolute top-full left-0 right-0 z-50 border-t border-brand-100 dark:border-brand-900 bg-white dark:bg-brand-950 shadow-xl shadow-brand-600/5">
      <div className="max-w-7xl mx-auto px-8 py-8 grid grid-cols-12 gap-8">
        <div
          className={`${m.feat ? 'col-span-8' : 'col-span-12'} grid gap-8`}
          style={{ gridTemplateColumns: `repeat(${m.cols.length}, 1fr)` }}
        >
          {m.cols.map((col, ci) => (
            <div key={ci}>
              <p className="text-[10px] font-bold text-brand-600 uppercase tracking-[0.14em] mb-4 pb-2 border-b border-brand-100 dark:border-brand-900">
                {isPt ? col.head_pt : col.head_en}
              </p>
              <ul className="space-y-0.5">
                {col.links.map((link, li) => (
                  <li key={li}>
                    <button
                      onClick={() => { onNavigate(link.href); onClose(); }}
                      className="w-full text-left flex items-center justify-between px-3 py-2.5 rounded-xl text-[13.5px] text-brand-800 dark:text-brand-200 hover:bg-brand-50 dark:hover:bg-brand-900 hover:text-brand-600 dark:hover:text-brand-400 transition-colors group"
                    >
                      <span>{isPt ? link.pt : link.en}</span>
                      <IcoArrow cls="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-brand-600 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {m.feat && (
          <button
            onClick={() => { onNavigate(m.feat.href); onClose(); }}
            className="col-span-4 rounded-2xl bg-brand-600 p-7 text-left flex flex-col justify-between group hover:bg-brand-700 transition-colors"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-5">
                <img
                  src="/fdc-logo.png"
                  alt="Logo"
                  className="w-6 h-6 object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              <h4 className="font-bold text-white text-lg leading-snug mb-3 whitespace-pre-line">
                {isPt ? m.feat.title_pt : m.feat.title_en}
              </h4>
              <p className="text-white/70 text-sm leading-relaxed">
                {isPt ? m.feat.desc_pt : m.feat.desc_en}
              </p>
            </div>
            <div className="flex items-center gap-2 text-white text-sm font-semibold mt-6 group-hover:gap-3 transition-all">
              <span>{isPt ? 'Saber mais' : 'Learn more'}</span>
              <IcoArrow cls="w-4 h-4" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Navbar ─────────────────────────────────────────────── */
export default function Navbar({ onNavigate, currentPage, darkHero = false }) {
  const { i18n } = useTranslation();
  const { isDark, toggle } = useDarkMode();
  const scrolled = useScrolled();
  const [active, setActive] = useState(null);
  const [mob, setMob] = useState(false);
  const [mobExp, setMobExp] = useState(null);
  const navRef = useRef(null);
  const timer = useRef(null);
  const isPt = i18n.language === 'pt';

  const close = useCallback(() => {
    clearTimeout(timer.current);
    setActive(null);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [close]);

  // Bloqueia scroll do body quando drawer está aberto
  useEffect(() => {
    if (mob) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mob]);

  const navItems = [
    { key: 'quemSomos', pt: 'Quem Somos',     en: 'About Us',       href: '/quem-somos', mega: true  },
    { key: 'programas', pt: 'Programas',      en: 'Programmes',     href: '/programas',  mega: true  },
    { key: 'accoes',    pt: 'Acções Sociais', en: 'Social Actions', href: '/accoes',     mega: true  },
    { key: 'impacto',   pt: 'Impacto',        en: 'Impact',         href: '/impacto',    mega: false },
    { key: 'parceiros', pt: 'Parceiros',      en: 'Partners',       href: '/parceiros',  mega: false },
    { key: 'contacto',  pt: 'Contacto',       en: 'Contact',        href: '/contacto',   mega: false },
  ];

  const go = (href) => {
    close();
    setMob(false);
    onNavigate && onNavigate(href);
  };

  const isScrolledOrOpen = scrolled || active !== null;
  const useWhite = darkHero && !isScrolledOrOpen;

  return (
    <>
      <header
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolledOrOpen ? 'glass-nav shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between gap-6">

          <Logo onClick={() => go('/')} white={useWhite} />

          {/* Desktop nav — só visível em lg+ */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {navItems.map((item) => {
              const isOpen    = active === item.key;
              const isCurrent = currentPage === item.href;
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => {
                    if (!item.mega) return;
                    clearTimeout(timer.current);
                    setActive(item.key);
                  }}
                  onMouseLeave={() => {
                    if (!item.mega) return;
                    timer.current = setTimeout(() => setActive(null), 160);
                  }}
                >
                  <button
                    onClick={() => { close(); go(item.href); }}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[13.5px] font-medium transition-all duration-150 ${
                      useWhite
                        ? isOpen || isCurrent
                          ? 'bg-white/20 text-white'
                          : 'text-white/90 hover:bg-white/15 hover:text-white'
                        : isOpen || isCurrent
                          ? 'bg-brand-50 dark:bg-brand-900 text-brand-700 dark:text-brand-300'
                          : 'text-brand-800 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/60 hover:text-brand-700 dark:hover:text-brand-200'
                    }`}
                  >
                    {isPt ? item.pt : item.en}
                    {item.mega && (
                      <IcoChevronDown cls={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                </div>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">

            {/* Language — escondido em mobile */}
            <button
              onClick={() => i18n.changeLanguage(isPt ? 'en' : 'pt')}
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold transition-all ${
                useWhite
                  ? 'border-white/30 hover:border-white'
                  : 'border-brand-200 dark:border-brand-800 hover:border-brand-500'
              }`}
            >
              <span className={isPt
                ? useWhite ? 'text-white font-black' : 'text-brand-600 dark:text-brand-400 font-black'
                : useWhite ? 'text-white/40' : 'opacity-40'
              }>PT</span>
              <span className={useWhite ? 'text-white/30' : 'text-brand-300 dark:text-brand-700'}>/</span>
              <span className={!isPt
                ? useWhite ? 'text-white font-black' : 'text-brand-600 dark:text-brand-400 font-black'
                : useWhite ? 'text-white/40' : 'opacity-40'
              }>EN</span>
            </button>

            {/* Dark mode — escondido em mobile */}
            <button
              onClick={toggle}
              className={`hidden sm:flex p-2.5 rounded-full transition-all ${
                useWhite
                  ? 'text-white/80 hover:bg-white/15 hover:text-white'
                  : 'text-brand-500 dark:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900 hover:text-brand-600 dark:hover:text-brand-400'
              }`}
            >
              {isDark ? <IcoSun cls="w-[18px] h-[18px]" /> : <IcoMoon cls="w-[18px] h-[18px]" />}
            </button>

            {/* CTA — escondido em mobile */}
            <button
              onClick={() => go('/contacto')}
              className={`hidden lg:inline-flex py-2.5 px-5 text-[13px] rounded-full font-semibold transition-all ${
                useWhite
                  ? 'bg-white text-brand-700 hover:bg-white/90'
                  : 'btn-primary'
              }`}
            >
              {isPt ? 'Apoiar' : 'Support'}
            </button>

            {/* Hamburger — só em mobile */}
            <button
              onClick={() => setMob((o) => !o)}
              className={`lg:hidden p-2.5 rounded-full transition-all ${
                useWhite
                  ? 'text-white hover:bg-white/15'
                  : 'text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900'
              }`}
            >
              {mob ? <IcoClose /> : <IcoMenu />}
            </button>
          </div>
        </div>

        {/* Mega panels — só desktop */}
        {navItems.filter((i) => i.mega).map((item) =>
          active === item.key ? (
            <div
              key={item.key}
              onMouseEnter={() => { clearTimeout(timer.current); setActive(item.key); }}
              onMouseLeave={() => { timer.current = setTimeout(() => setActive(null), 160); }}
            >
              <MegaPanel menuKey={item.key} isPt={isPt} onNavigate={go} onClose={close} />
            </div>
          ) : null
        )}
      </header>

      {/* Click-outside overlay — desktop */}
      {active && (
        <div className="fixed inset-0 z-40" onClick={close} />
      )}

      {/* ── Mobile drawer ─────────────────────────────────── */}
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${
          mob ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMob(false)}
        />

        {/* Drawer panel */}
        <div className={`absolute top-0 left-0 bottom-0 w-[300px] bg-white dark:bg-brand-950 flex flex-col shadow-2xl transition-transform duration-300 ${
          mob ? 'translate-x-0' : '-translate-x-full'
        }`}>

          {/* Header */}
          <div className="flex items-center justify-between px-5 h-[68px] border-b border-black/10 dark:border-white/10 flex-shrink-0">
            <Logo onClick={() => go('/')} white={false} />
            <button
              onClick={() => setMob(false)}
              className="p-2 rounded-full text-black/50 hover:bg-black/5 dark:text-white/50 dark:hover:bg-white/10 transition-all"
            >
              <IcoClose cls="w-5 h-5" />
            </button>
          </div>

          {/* Links */}
          <div className="flex-1 overflow-y-auto py-2">
            {navItems.map((item) => (
              <div key={item.key}>
                <div className="flex items-center border-b border-black/5 dark:border-white/5 last:border-0">
                  <button
                    onClick={() => go(item.href)}
                    className="flex-1 text-left px-5 py-4 text-[14px] font-semibold text-black dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                  >
                    {isPt ? item.pt : item.en}
                  </button>
                  {item.mega && (
                    <button
                      onClick={() => setMobExp((e) => (e === item.key ? null : item.key))}
                      className="pr-5 py-4 text-black/30 dark:text-white/30"
                    >
                      <IcoChevronDown cls={`w-4 h-4 transition-transform duration-200 ${mobExp === item.key ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {mobExp === item.key && megaData[item.key] && (
                  <div className="bg-black/[0.03] dark:bg-white/[0.03] px-5 py-2">
                    {megaData[item.key].cols.flatMap((c) => c.links).map((link, li) => (
                      <button
                        key={li}
                        onClick={() => go(link.href)}
                        className="w-full text-left py-2.5 text-[13px] text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white border-b border-black/5 dark:border-white/5 last:border-0 transition-colors"
                      >
                        {isPt ? link.pt : link.en}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer do drawer */}
          <div className="p-5 border-t border-black/10 dark:border-white/10 space-y-3 flex-shrink-0">
            {/* Language toggle */}
            <button
              onClick={() => i18n.changeLanguage(isPt ? 'en' : 'pt')}
              className="w-full py-3 border border-black/15 dark:border-white/15 rounded-xl text-[13px] font-semibold text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/5 transition-all"
            >
              {isPt ? 'Switch to English' : 'Mudar para Português'}
            </button>
            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              className="w-full py-3 border border-black/15 dark:border-white/15 rounded-xl text-[13px] font-semibold text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/5 transition-all flex items-center justify-center gap-2"
            >
              {isDark ? <IcoSun cls="w-4 h-4" /> : <IcoMoon cls="w-4 h-4" />}
              {isDark ? (isPt ? 'Modo Claro' : 'Light Mode') : (isPt ? 'Modo Escuro' : 'Dark Mode')}
            </button>
            {/* CTA */}
            <button
              onClick={() => go('/contacto')}
              className="btn-primary w-full py-3"
            >
              {isPt ? 'Apoiar a Fundação' : 'Support the Foundation'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}